// AllocateTemplateWizard — allocate a Form Template to a CLIENT as a reusable, blank
// "report template" placed inside a standardised project folder. Distinct from the
// recipient-issuance PublishWizard: this creates a standing template that reports are
// generated FROM repeatedly. Choose client → all projects / one project → destination
// folder (pick existing or create new — a new folder is materialised across ALL the
// client's projects) → Completed by (Client / PR).
import { useEffect, useMemo, useState } from "react";
import { X, Loader2, FolderOpen, Check, FilePlus2, FolderPlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import FolderTree from "@/components/documents/FolderTree";

const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,#C75A1B)]";
const INTERNAL_ROLES = new Set(["director", "administrator", "consultant"]);

export default function AllocateTemplateWizard({ source, user, clients: clientsProp, onClose, onAllocated }) {
    const isInternal = user && INTERNAL_ROLES.has(user.role);
    const [clients, setClients] = useState(clientsProp || []);
    const [projects, setProjects] = useState([]);
    const [folders, setFolders] = useState([]);
    const [busy, setBusy] = useState(false);

    const [clientId, setClientId] = useState(isInternal ? "" : (user?.client_id || (user?.client_ids || [])[0] || ""));
    const [scope, setScope] = useState("all_projects");
    const [projectId, setProjectId] = useState("");
    const [folderMode, setFolderMode] = useState("existing");   // existing | new
    const [folderId, setFolderId] = useState("");
    const [newFolderName, setNewFolderName] = useState("");
    const [completedBy, setCompletedBy] = useState("client");
    const [portalVisible, setPortalVisible] = useState(true);
    const [title, setTitle] = useState(source?.name || "");

    useEffect(() => { if (!clientsProp?.length && isInternal) api.get("/clients").then((r) => setClients(r.data)).catch(() => { }); }, [clientsProp, isInternal]);

    // Load the client's projects (for the scope=project picker + folder-tree preview).
    useEffect(() => {
        if (!clientId) { setProjects([]); return; }
        api.get("/projects", { params: { client_id: clientId } }).then((r) => setProjects(r.data)).catch(() => setProjects([]));
    }, [clientId]);

    // Folder tree is identical across the client's projects, so preview a representative one:
    // the selected project (scope=project) or the client's first project (scope=all).
    const previewProjectId = useMemo(
        () => (scope === "project" ? projectId : (projects[0]?.id || "")),
        [scope, projectId, projects]
    );
    useEffect(() => {
        if (!clientId || !previewProjectId) { setFolders([]); return; }
        api.get("/folders", { params: { client_id: clientId, project_id: previewProjectId, scope: "project" } })
            .then((r) => setFolders(r.data)).catch(() => setFolders([]));
    }, [clientId, previewProjectId]);

    const folderName = useMemo(() => folders.find((f) => f.id === folderId)?.name, [folders, folderId]);

    async function submit() {
        if (!clientId) return toast.error("Choose a client");
        if (scope === "project" && !projectId) return toast.error("Choose a project");
        if (folderMode === "existing" && !folderId) return toast.error("Select a destination folder");
        if (folderMode === "new" && !newFolderName.trim()) return toast.error("Name the new folder");
        setBusy(true);
        try {
            const body = {
                template_id: source.id, client_id: clientId, scope,
                project_id: scope === "project" ? projectId : null,
                completed_by: completedBy, portal_visible: portalVisible,
                title: title.trim() || source.name,
                ...(folderMode === "existing" ? { folder_id: folderId } : { folder_name: newFolderName.trim() }),
            };
            const { data } = await api.post("/template-allocations", body);
            toast.success("Template allocated — reports can now be generated from it");
            onAllocated?.(data);
            onClose();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not allocate template");
        }
        setBusy(false);
    }

    return (
        <div className="fixed inset-0 z-[60] bg-black/45 grid place-items-center p-4" data-testid="allocate-template-wizard">
            <div className="bg-white rounded-[16px] shadow-xl w-full max-w-[640px] max-h-[92vh] flex flex-col border" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "#E5E0DA" }}>
                    <div>
                        <h3 className="text-base font-bold text-[#1F1F1F]">Allocate as template</h3>
                        <div className="text-[11.5px] text-[#9A968F]">Form: {source?.name} — reports get generated from this repeatedly</div>
                    </div>
                    <button data-testid="allocate-wizard-close" onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X size={16} /></button>
                </div>

                <div className="p-5 overflow-y-auto space-y-4 flex-1">
                    <div className="grid grid-cols-2 gap-3">
                        {isInternal && (
                            <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Client
                                <select data-testid="allocate-client" value={clientId} onChange={(e) => { setClientId(e.target.value); setProjectId(""); setFolderId(""); }} className={inputCls + " mt-1 font-normal normal-case"}>
                                    <option value="">— Select —</option>
                                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </label>
                        )}
                        <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Completed by
                            <select data-testid="allocate-completed-by" value={completedBy} onChange={(e) => setCompletedBy(e.target.value)} className={inputCls + " mt-1 font-normal normal-case"}>
                                <option value="client">Client (site managers self-serve)</option>
                                <option value="pr">PR staff (completed on a visit)</option>
                            </select>
                        </label>
                    </div>

                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] block">Template title
                        <input data-testid="allocate-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={source?.name} className={inputCls + " mt-1 font-normal normal-case"} />
                    </label>

                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1.5">Allocate to</div>
                        <div className="grid grid-cols-2 gap-2">
                            {[
                                { key: "all_projects", label: "All projects", help: "Living rule — new projects inherit it too" },
                                { key: "project", label: "One project", help: "A single project only" },
                            ].map((o) => (
                                <button key={o.key} data-testid={`allocate-scope-${o.key}`} onClick={() => setScope(o.key)}
                                    className={`text-left p-3 rounded-[10px] border transition-colors ${scope === o.key ? "border-[var(--brand-accent,#C75A1B)] bg-[#FBEFE5]" : "border-[#E5E0DA] bg-white hover:bg-[#F6F4F0]"}`}>
                                    <div className="text-[12.5px] font-bold text-[#1F1F1F]">{o.label}</div>
                                    <div className="text-[11px] text-[#6F6A64] mt-0.5">{o.help}</div>
                                </button>
                            ))}
                        </div>
                        {scope === "project" && (
                            <select data-testid="allocate-project" value={projectId} onChange={(e) => setProjectId(e.target.value)} disabled={!clientId} className={inputCls + " mt-2 disabled:bg-[#F6F4F0]"}>
                                <option value="">{clientId ? "— Select a project —" : "Choose a client first"}</option>
                                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        )}
                    </div>

                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1.5">Destination folder <span className="text-red-500">*</span></div>
                        <div className="inline-flex rounded-lg border border-[#E5E0DA] overflow-hidden mb-2">
                            <button data-testid="allocate-folder-existing" onClick={() => setFolderMode("existing")}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold ${folderMode === "existing" ? "bg-[#FBEFE5] text-[#9F4212]" : "bg-white text-[#6F6A64]"}`}>
                                <FolderOpen size={13} /> Pick existing
                            </button>
                            <button data-testid="allocate-folder-new" onClick={() => setFolderMode("new")}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold border-l border-[#E5E0DA] ${folderMode === "new" ? "bg-[#FBEFE5] text-[#9F4212]" : "bg-white text-[#6F6A64]"}`}>
                                <FolderPlus size={13} /> New folder
                            </button>
                        </div>
                        {folderMode === "existing" ? (
                            <div className="border rounded-[10px] p-2.5 max-h-[220px] overflow-y-auto" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }} data-testid="allocate-folder-picker">
                                {!clientId ? (
                                    <p className="text-[12px] italic text-[#9A968F] py-3 text-center">Choose a client first.</p>
                                ) : folders.length === 0 ? (
                                    <p className="text-[12px] italic text-[#9A968F] py-3 text-center">No folders yet — create a new one.</p>
                                ) : (
                                    <FolderTree folders={folders} selectedId={folderId} onSelect={setFolderId} canEdit={false} isInternal hidePseudo />
                                )}
                                {folderId && <div className="text-[12px] text-[#3F7D3A] mt-1.5 font-semibold flex items-center gap-1"><Check size={13} /> Filing into: {folderName}</div>}
                            </div>
                        ) : (
                            <div>
                                <input data-testid="allocate-new-folder-name" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="e.g. Permits to Work" className={inputCls} />
                                <p className="text-[11px] text-[#9A968F] mt-1.5">This folder will be created in <b>every</b> one of the client's projects, keeping the trees identical.</p>
                            </div>
                        )}
                    </div>

                    <label className="flex items-center gap-2 text-[12.5px] text-[#1F1F1F]">
                        <input data-testid="allocate-portal-visible" type="checkbox" checked={portalVisible} onChange={(e) => setPortalVisible(e.target.checked)} /> Visible to the client's portal users
                    </label>
                </div>

                <div className="px-5 py-3 border-t flex items-center justify-end gap-2 bg-[#F5F3F0]" style={{ borderColor: "#E5E0DA" }}>
                    <button onClick={onClose} className="px-3 py-2 text-sm text-[#6F6A64] hover:text-[#1F1F1F]">Cancel</button>
                    <button data-testid="allocate-submit" onClick={submit} disabled={busy}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg text-white disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <FilePlus2 size={14} />} Allocate template
                    </button>
                </div>
            </div>
        </div>
    );
}
